import React, { Suspense } from "react";
import { Box } from "lucide-react";

const DEFAULT_ICON_KEY = "box";
export const DEFAULT_ICON_NAME = "Box";

const LEGACY_ICON_ALIASES = {
  airplane: "plane",
  "baby-bottle": "baby",
  basket: "shopping-basket",
  plate: "utensils",
  shoe: "footprints",
  soap: "soup",
  spa: "space",
};

const normalizeIconInput = (iconName) =>
  String(iconName || "")
    .trim()
    .replace(/Icon$/i, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Za-z])([0-9])/g, "$1-$2")
    .replace(/([0-9])([A-Za-z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();

const loadDynamicIconImports = () => {
  try {
    return require("lucide-react/dynamicIconImports").default;
  } catch (_error) {
    const lucide = require("lucide-react");
    const fallbackImports = {};

    for (const exportName of Object.keys(lucide.icons || {})) {
      const iconKey = normalizeIconInput(exportName);
      const IconComponent = lucide[exportName] || lucide[`${exportName}Icon`];
      if (!IconComponent) continue;
      fallbackImports[iconKey] = () =>
        Promise.resolve({
          default: IconComponent,
        });
    }

    return fallbackImports;
  }
};

const dynamicIconImports = loadDynamicIconImports();

const toStoredIconName = (iconKey) =>
  iconKey
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

const toIconLabel = (iconKey) =>
  iconKey
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const iconMetadataByKey = new Map();
const normalizedIconNamesToKey = new Map();

for (const iconKey of Object.keys(dynamicIconImports)) {
  const name = toStoredIconName(iconKey);
  const label = toIconLabel(iconKey);
  const metadata = {
    key: iconKey,
    name,
    label,
    searchValue: `${iconKey} ${name} ${label}`.toLowerCase(),
  };

  iconMetadataByKey.set(iconKey, metadata);
  normalizedIconNamesToKey.set(iconKey, iconKey);
  normalizedIconNamesToKey.set(name.toLowerCase(), iconKey);
  normalizedIconNamesToKey.set(label.toLowerCase(), iconKey);
}

for (const [legacyName, iconKey] of Object.entries(LEGACY_ICON_ALIASES)) {
  normalizedIconNamesToKey.set(legacyName, iconKey);
}

export const resolveLucideIconKey = (iconName) => {
  const normalized = normalizeIconInput(iconName);
  if (!normalized) return DEFAULT_ICON_KEY;

  return (
    normalizedIconNamesToKey.get(normalized) ||
    normalizedIconNamesToKey.get(LEGACY_ICON_ALIASES[normalized]) ||
    DEFAULT_ICON_KEY
  );
};

const getIconMetadata = (iconName) =>
  iconMetadataByKey.get(resolveLucideIconKey(iconName)) ||
  iconMetadataByKey.get(DEFAULT_ICON_KEY);

export const getIconLabel = (iconName) => getIconMetadata(iconName)?.label || DEFAULT_ICON_NAME;

const lazyIconCache = new Map();

const getLazyIconComponent = (iconName) => {
  const iconKey = resolveLucideIconKey(iconName);

  if (!lazyIconCache.has(iconKey)) {
    const loader = dynamicIconImports[iconKey] || dynamicIconImports[DEFAULT_ICON_KEY];
    lazyIconCache.set(
      iconKey,
      React.lazy(() =>
        loader().then((module) => ({
          default: module.default,
        }))
      )
    );
  }

  return lazyIconCache.get(iconKey);
};

const BoxFallback = ({ size = 24, className, strokeWidth, ...props }) => (
  <Box size={size} className={className} strokeWidth={strokeWidth} {...props} />
);

export const LucideIcon = ({ iconName, fallback, ...props }) => {
  const LazyIcon = getLazyIconComponent(iconName);

  return (
    <Suspense fallback={fallback ?? <BoxFallback {...props} />}>
      <LazyIcon {...props} />
    </Suspense>
  );
};

export const icons = Array.from(iconMetadataByKey.values())
  .sort((left, right) => left.label.localeCompare(right.label))
  .map((icon) => ({
    ...icon,
    component: function IconComponent(props) {
      return <LucideIcon iconName={icon.name} {...props} />;
    },
  }));
